import { useMemo, useState } from "react";
import { Link, useOutletContext, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { diffWords } from "diff";
import { ChevronLeft } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DesktopOnly } from "@/components/studio/DesktopOnly";
import { api } from "@/lib/api";
import { fmtNum, plainText } from "@/lib/studio";

const useDraftItems = (did) => useQuery({ queryKey: ["draft-items", did, "content"], queryFn: () => api.get(`/drafts/${did}/items`, { params: { content: true } }).then((r) => r.data.items.filter((i) => i.kind === "chapter")), enabled: !!did });

const STATUS = { changed: ["Changed", "#FBBF24"], added: ["New in B", "#10B981"], removed: ["Removed in B", "#F87171"], same: ["Unchanged", "#A8A090"] };

const DiffView = ({ a, b }) => (
  <div className="whitespace-pre-wrap rounded-md border border-[#2D3442] bg-[#0F131A] p-5 font-serif text-[15px] leading-7 text-[#D6CCBC]" data-testid="diff-view">
    {diffWords(plainText(a), plainText(b)).map((p, i) => (
      <span key={i} className={p.added ? "bg-[#10B981]/20 text-[#A7F3D0]" : p.removed ? "bg-[#F87171]/20 text-[#FCA5A5] line-through" : ""}>{p.value}</span>
    ))}
  </div>
);

const DraftPick = ({ label, value, onChange, drafts, testid }) => (
  <div className="flex items-center gap-2"><span className="lf-label">{label}</span>
    <Select value={value} onValueChange={onChange}><SelectTrigger className="h-9 w-48" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>{drafts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent></Select>
  </div>
);

export default function ComparePage() {
  const { base } = useOutletContext();
  const { sid } = useParams();
  const { data: story } = useQuery({ queryKey: ["story", sid], queryFn: () => api.get(`/stories/${sid}`).then((r) => r.data) });
  const drafts = story?.drafts || [];
  const [a, setA] = useState(null);
  const [b, setB] = useState(null);
  const [open, setOpen] = useState(null);
  const aId = a || drafts[drafts.length - 2]?.id, bId = b || drafts[drafts.length - 1]?.id;
  const { data: A = [] } = useDraftItems(aId);
  const { data: B = [] } = useDraftItems(bId);
  const rows = useMemo(() => {
    const byA = Object.fromEntries(A.map((c) => [c.lineage_id, c]));
    const seen = new Set();
    const out = B.map((c) => { seen.add(c.lineage_id); const o = byA[c.lineage_id]; return { key: c.lineage_id, a: o, b: c, status: !o ? "added" : o.content === c.content && o.title === c.title ? "same" : "changed" }; });
    return [...out, ...A.filter((c) => !seen.has(c.lineage_id)).map((c) => ({ key: c.lineage_id, a: c, b: null, status: "removed" }))];
  }, [A, B]);
  return (
    <DesktopOnly backTo={`${base}/studio/${sid}`} backLabel="Back to story">
      <div className="mx-auto max-w-5xl px-10 py-10" data-testid="compare-page">
        <Link to={`${base}/studio/${sid}`} className="inline-flex items-center gap-1 text-xs text-[#A8A090] hover:text-[#F3EBDD]" data-testid="compare-back"><ChevronLeft className="h-3.5 w-3.5" />Back to storyboard</Link>
        <h1 className="mt-4 font-display text-5xl text-[#F3EBDD]">Compare drafts</h1>
        <div className="mt-6 flex flex-wrap gap-6">
          <DraftPick label="A" value={aId} onChange={setA} drafts={drafts} testid="compare-a" />
          <DraftPick label="B" value={bId} onChange={setB} drafts={drafts} testid="compare-b" />
        </div>
        <div className="mt-8 space-y-2">
          {rows.map((r) => {
            const delta = (r.b?.word_count || 0) - (r.a?.word_count || 0);
            return (
              <div key={r.key} className="rounded-md border border-[#2D3442] bg-[#13171F]" data-testid={`compare-row-${r.key}`}>
                <button onClick={() => setOpen(open === r.key ? null : r.key)} className="flex w-full items-center gap-4 px-4 py-3 text-left">
                  <span className="flex-1 text-[#F3EBDD]">{r.b?.title || r.a?.title || "Untitled chapter"}{r.a && r.b && r.a.title !== r.b.title && <span className="ml-2 text-xs text-[#A8A090]">(was “{r.a.title || "untitled"}”)</span>}</span>
                  <span className="text-xs" style={{ color: STATUS[r.status][1] }} data-testid={`compare-status-${r.key}`}>{STATUS[r.status][0]}</span>
                  <span className="w-28 text-right text-xs text-[#A8A090]">{fmtNum(r.b?.word_count ?? r.a?.word_count)} words {delta !== 0 && <b className={delta > 0 ? "text-[#10B981]" : "text-[#F87171]"}>{delta > 0 ? "+" : ""}{delta}</b>}</span>
                </button>
                {open === r.key && <div className="px-4 pb-4"><DiffView a={r.a?.content} b={r.b?.content} /></div>}
              </div>
            );
          })}
          {rows.length === 0 && <p className="text-sm text-[#A8A090]" data-testid="compare-empty">No chapters to compare yet.</p>}
        </div>
      </div>
    </DesktopOnly>
  );
}
